import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import { ResponsiveContainer } from "recharts";
import { CHART_TYPES } from "../../../core/constants/chartTypes";
import { CHARTICONS } from "../../../core/constants/chartStyles";
import { DataNormalizer } from "../../../core/utils/dataNormalizer";
import { ChartFactory } from "../charts/chartFactory/ChartFactory";

export default function DynamicChartViewer({
  possibleCharts,
  data,
  xAxis,
  yAxis,
  title,
  darkMode,
}) {
  const [chartType, setChartType] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const isValidAxes = DataNormalizer.isValidAxes(xAxis, yAxis);
  const hasTable = possibleCharts.some((c) => c.toLowerCase() === "table");

  const normalizedData = DataNormalizer.normalize(data, xAxis, yAxis);
  const total = DataNormalizer.calculateTotal(normalizedData, yAxis);
  const graphs = possibleCharts.filter((t) =>
    CHART_TYPES.includes(t.toLowerCase()),
  );

  const getThemeColors = () => ({
    axisColor: darkMode ? "#e2e8f0" : "#1e293b",
    gridColor: darkMode ? "#475569" : "#e2e8f0",
    bgColor: darkMode ? "#1e293b" : "#f8fafc",
    barHover: darkMode ? "rgba(27, 38, 53, 0.85)" : "rgba(0, 0, 0, 0.2)",
  });

  const themeColors = getThemeColors();

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const { name, value } = payload[0];
    const percent = ["pie", "donut"].includes(chartType)
      ? ` (${((value / total) * 100).toFixed(1)}%)`
      : "";
    return (
      <Box
        sx={{
          bgcolor: darkMode ? "#334155" : "#fff",
          p: 1.5,
          borderRadius: 2,
          color: darkMode ? "#e2e8f0" : "#1e293b",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: "bold" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12 }}>
          {`${name}: ${value.toLocaleString("en-US", {
            maximumFractionDigits: 2,
          })}${percent}`}
        </Typography>
      </Box>
    );
  };

  const scatterTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <Box
        sx={{ bgcolor: darkMode ? "#334155" : "#fff", p: 1, borderRadius: 2 }}
      >
        <Typography sx={{ fontSize: 12 }}>{`${xAxis}: ${d[xAxis]}`}</Typography>
        <Typography sx={{ fontSize: 12 }}>{`${yAxis}: ${d[yAxis]}`}</Typography>
        {chartType === "bubble" && (
          <Typography sx={{ fontSize: 12 }}>{`Size: ${d[yAxis]}`}</Typography>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (!chartType && graphs.length) setChartType(graphs[0].toLowerCase());
  }, [graphs, chartType]);

  if (!isValidAxes && !hasTable) {
    return (
      <Typography sx={{ textAlign: "center", py: 3, color: "gray" }}>
        Invalid axis configuration. Please check your data schema.
      </Typography>
    );
  }

  if (!data?.length || graphs.length === 0) {
    return (
      <Typography sx={{ textAlign: "center", py: 3, color: "gray" }}>
        No data to display
      </Typography>
    );
  }

  const renderChart = () => {
    const chartProps = {
      normalizedData,
      xAxis,
      yAxis,
      title,
      darkMode,
      axisColor: themeColors.axisColor,
      gridColor: themeColors.gridColor,
      barHover: themeColors.barHover,
      CustomTooltip,
      chartType,
      data,
      activeIndex,
      setActiveIndex,
      total,
      scatterTooltip,
    };

    const chart = ChartFactory.create(chartType, chartProps);
    return chart ? chart.render() : null;
  };

  return (
    <Box
      sx={{
        p: 2,
        width: chartType === "table" ? "fit-content" : "100%",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: "column",
        height: chartType === "table" ? "auto" : "70vh",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            mb: 2,
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <InsightsIcon sx={{ color: "#fff" }} />
          {title ||
            `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`}
        </Typography>
        {graphs.length > 1 && (
          <FormControl
            size="small"
            sx={{
              minWidth: 130,
              ml: 10,
              mr: 1,
              backgroundColor: "transparent",
            }}
          >
            <InputLabel sx={{ color: darkMode ? "#e2e8f0" : "#1e293b" }}>
              Chart Type
            </InputLabel>
            <Select
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              label="Chart Type"
              sx={{
                color: darkMode ? "#e2e8f0" : "#1e293b",
                backgroundColor: "transparent",
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: darkMode ? "#475569" : "#cbd5e1",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4f46e5",
                },
              }}
            >
              {graphs.map((t) => (
                <MenuItem
                  key={t}
                  value={t.toLowerCase()}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {CHARTICONS[t.toLowerCase()] || null}
                  <Typography sx={{ lineHeight: 1 }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      <Box
        sx={{
          flex: chartType === "table" ? "0 0 auto" : 1,
          minHeight: chartType === "table" ? "auto" : 0,
          width: chartType === "table" ? "fit-content" : "100%",
          overflowX: chartType === "table" ? "auto" : "hidden",
          overflowY: chartType === "table" ? "auto" : "hidden",
          backgroundColor: "transparent",
        }}
      >
        {chartType === "table" ? (
          <Box sx={{ display: "inline-block" }}>{renderChart()}</Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
}
